import { type ReactNode } from "react";
import NextLink from "next/link";
import { Link, IconFa, Badge } from "@cruk/cruk-react-components";
import { faStar } from "@fortawesome/free-solid-svg-icons";

import { formatMoneyWithCommas } from "@fwa/src/utils/formatUtils";
import { userNameForPage } from "@fwa/src/utils/pageUtils";
import { imagePathFromImageType } from "@fwa/src/services/imageService";
import { memberPageIconUrlForTheme } from "@fwa/src/utils/memberUtils";
import { useBrandContext } from "@fwa/src/contexts/BrandContext";

import sharedStyles from "@fwa/src/components/styles.module.css";
import styles from "@fwa/src/components/MemberFundraisingPage/styles.module.css";

import { type FundraisingPageType } from "@fwa/src/types";

export const MemberFundraisingPage = ({
  fundraisingPage,
  isLeader = false,
  children,
}: {
  fundraisingPage: FundraisingPageType;
  isLeader?: boolean;
  children?: ReactNode;
}) => {
  const { fundraiser, title } = fundraisingPage;
  const [brandName] = useBrandContext();
  const name = userNameForPage(fundraisingPage);
  const raisedAmount = fundraisingPage?.donationsTotalAmount
    ? formatMoneyWithCommas(fundraisingPage?.donationsTotalAmount).replace(
        ".00",
        "",
      )
    : "0";
  const iconUrl = memberPageIconUrlForTheme(brandName);
  const profileImageUrl =
    fundraiser?.profileImage?.entityType === "ImageFacebook"
      ? `${fundraiser.profileImage.url}?width=300&height=300`
      : fundraiser?.profileImage
        ? imagePathFromImageType(fundraiser?.profileImage)
        : "";

  return (
    <div
      className={styles["shadow-wrapper"]}
      data-component="member-fundraising-page"
    >
      <NextLink
        href={`/page${fundraisingPage.url.slice(
          fundraisingPage.url.lastIndexOf("/"),
        )}`}
        data-cta-type="link-page"
      >
        <div
          className={styles["member-link-span"]}
          aria-label={`${fundraisingPage.title}`}
        >
          <div
            className={sharedStyles["row"]}
            style={{ gap: "var(--spacing-xs, 8px)" }}
          >
            <div style={{ position: "relative" }}>
              <div
                className={styles["profile-image-wrapper"]}
                data-is-leader={isLeader}
              >
                {fundraiser.profileImage ? (
                  <img
                    className={styles["profile-image"]}
                    src={profileImageUrl}
                    alt=""
                  />
                ) : (
                  <img src={iconUrl} alt="" />
                )}
              </div>
              {!!isLeader && (
                <div className={styles["leader-star"]}>
                  <Badge backgroundColor="secondary" size="s">
                    <IconFa faIcon={faStar} color="#FFF" size="2em" />
                  </Badge>
                </div>
              )}
            </div>

            <div
              className={[styles["text-wrapper"], "spacing-props"].join(" ")}
              data-margin-left="xs"
            >
              <p
                className={[
                  sharedStyles["single-line-ellipsis"],
                  "spacing-props",
                  "text-props",
                ].join(" ")}
                data-text-weight="heavy"
                data-margin-vertical="none"
              >
                {`${name}${isLeader ? " (Team Leader)" : ""}`}
              </p>
              <p
                className={[
                  sharedStyles["single-line-ellipsis"],
                  "spacing-props",
                ].join(" ")}
                data-margin-vertical="none"
              >
                <Link
                  as="span"
                  className={[
                    sharedStyles["single-line-ellipsis"],
                    "component-link",
                  ].join(" ")}
                >{`${title}`}</Link>
              </p>
              <p
                className={[
                  sharedStyles["single-line-ellipsis"],
                  "spacing-props",
                ].join(" ")}
                data-margin-vertical="none"
              >
                {`£${raisedAmount} raised`}
              </p>
            </div>
          </div>
        </div>
      </NextLink>

      {children}
    </div>
  );
};

export default MemberFundraisingPage;
